import { useAppDispatch } from '_store';
import { PaginationComponent } from '_store/components/PaginationComponent';
import usePagination from '_store/components/usePagination';
import ivimsReducer from '_store/ivim/ivimsReducers';
import { Ivim, IvimStatusFilter } from '_store/ivim/ivimsTypes';
import {
  ivimFilterStatusSelector,
  ivimsSelector,
  paginationCurrentPageSelector,
  paginationItemsPerPageSelector,
  totalIvimsSelector,
} from '_store/ivim/selectors';
import { getIvimTitle, useIvimsRequest } from '_store/ivim/utils';
import { useLocalization } from '@fluent/react';
import { Box, Button, CardContent } from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { CheckboxItem } from "../../../items/CheckboxItem";
import MessageDialog from '../../../items/MessageDialog';
import { RoleGuard } from '../../../items/RoleGuard';
import { useRolePermissions } from '../../../../hooks/useRolePermissions';
import CardListItem from './Card';
import { ExportCSV } from './ExportCSV';
import { IvimListFilters } from './Filters';
import styles from './styles';

export interface IvimListLayoutProps {
  onUpdateIvim?: (idExpanded: string) => void;
  onCloneIvim?: (idExpanded: string) => void;
}

// expanded details inside Ivim list items
const IvimListLayout: FC<IvimListLayoutProps> = (crudProps) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canWriteIvim, blockedActionMode } = useRolePermissions();
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const ivims = useSelector(ivimsSelector);
  const ivimStatus = useSelector(ivimFilterStatusSelector);
  const totalItems = useSelector(totalIvimsSelector);
  const paginationCurrentPage = useSelector(paginationCurrentPageSelector);
  const paginationItemsPerPage = useSelector(paginationItemsPerPageSelector);

  const [itemsPerPage, setItemsPerPage] = useState(paginationItemsPerPage);

  const { currentPage, totalPages, goToPage } = usePagination(
    totalItems,
    itemsPerPage,
    paginationCurrentPage,
  );
  const dispatchIvimsRequest = useIvimsRequest({ currentPage, itemsPerPage });

  useEffect(() => {
    dispatchIvimsRequest();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    dispatchIvimsRequest();
  }, []);

  useEffect(() => {
    if (paginationCurrentPage !== currentPage) {
      goToPage(paginationCurrentPage);
    }
  }, [paginationCurrentPage]);

  const [checked, setChecked] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [filteredStatus, setFilteredStatus] = useState(ivimStatus);
  const [showMessage, setShowMessage] = useState({
    active: false,
    typeAction: '',
    idsToDelete: null,
    typeEvent: 'ivim',
    idsToDisable: null,
  });

  const filterIvim = (val: Ivim) => {
    const searchVal = searchFilter.toLowerCase();
    const itemVal = val.id.toString().toLowerCase();
    return itemVal.indexOf(searchVal) > -1;
  };

  const handleCheckedChange = (id: string) => {
    if (checked.includes(id)) {
      setChecked(checked.filter((i) => i !== id));
    } else {
      setChecked([...checked, id]);
    }
  };

  const handleBulkCheckedChange = () => {
    if (checked.length === ivims.length) {
      setChecked([]);
    } else {
      setChecked(ivims.map((i) => i.id));
    }
  };

  const onMessageLeaveConfirmed = (type: string) => {
    if (type === 'confirm' && showMessage.typeAction === 'delete') {
      dispatch(ivimsReducer.actions.deleteIVIMs(showMessage.idsToDelete));
    }

    if (type === 'confirm' && showMessage.typeAction === 'disable') {
      dispatch(ivimsReducer.actions.disableIVIM({ ids: showMessage.idsToDisable, disable: true }));
    }

    if (type === 'confirm' && showMessage.typeAction === 'enable') {
      dispatch(ivimsReducer.actions.disableIVIM({ ids: showMessage.idsToDisable, disable: false }));
    }

    setShowMessage((sm) => ({
      ...sm,
      active: false,
      idsToDelete: null,
      idsToDisable: null,
    }));
  };

  const onDeleteIvim = (ids: string[]) => {
    setShowMessage((sm) => ({
      ...sm,
      typeAction: 'delete',
      active: true,
      idsToDelete: ids,
      count: ids.length,
    }));
  };

  const onDisableIvim = (ids: string[]) => {
    setShowMessage((sm) => ({
      ...sm,
      typeAction: 'disable',
      active: true,
      idsToDisable: ids,
      count: ids.length,
    }));
  };

  const onEnableIvim = (ids: string[]) => {
    setShowMessage((sm) => ({
      ...sm,
      typeAction: 'enable',
      active: true,
      idsToDisable: ids,
      count: ids.length,
    }));
  };

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    goToPage(value);
  };

  const ivimText = (ivim: Ivim) => getIvimTitle(ivim, l10n);

  return (
    <CardContent className={classes.cardContent} style={{ overflow: 'hidden' }}>
      {showMessage.active === true ? (
        <MessageDialog statusMessage={showMessage} onCloseDialog={onMessageLeaveConfirmed} />
      ) : null}

      <IvimListFilters
        searchFilter={searchFilter}
        setSearchFilter={setSearchFilter}
        setFilteredStatus={setFilteredStatus}
        setChecked={setChecked}
      />

      <ExportCSV show={ivims.length > 0} filename={'ivims'} />

      {ivims.length > 0 && (
        <Box marginBottom={2} hidden={checked.length <= 0} display={'flex'} flexDirection={'row'}>
          <CheckboxItem
            checked={checked.length === ivims.length}
            itemId={'all'}
            handleCheckedChange={handleBulkCheckedChange}
            size="small"
            label={
              checked.length === ivims.length
                ? l10n.getString('forms-event-deselect-all')
                : l10n.getString('forms-event-select-all')
            }
          />

          {filteredStatus !== IvimStatusFilter.CLOSED && (
            <>
              <RoleGuard
                allowed={canWriteIvim}
                blockedActionMode={blockedActionMode}
                blockedMessage={writeBlockedMessage}
              >
                <Button
                  onClick={() =>
                    filteredStatus === IvimStatusFilter.DISABLED
                      ? onEnableIvim(checked)
                      : onDisableIvim(checked)
                  }
                  disabled={checked.length <= 0}
                  color="primary"
                  variant="text"
                >
                  {filteredStatus === IvimStatusFilter.DISABLED
                    ? l10n.getString('forms-event-bulk-enable')
                    : l10n.getString('forms-event-bulk-disable')}
                </Button>
              </RoleGuard>
              <RoleGuard
                allowed={canWriteIvim}
                blockedActionMode={blockedActionMode}
                blockedMessage={writeBlockedMessage}
              >
                <Button
                  onClick={() => onDeleteIvim(checked)}
                  disabled={checked.length <= 0}
                  color="primary"
                  variant="text"
                >
                  {l10n.getString('forms-event-bulk-delete')}
                </Button>
              </RoleGuard>
            </>
          )}
        </Box>
      )}

      <div style={{ overflow: 'auto' }}>
        {ivims &&
          ivims
            .filter(filterIvim)
            .map((item) => (
              <CardListItem
                asCard={true}
                key={item.id}
                ivim={item}
                text={ivimText(item)}
                connectedDenms={Boolean(item.metadata.connectedDenms?.length > 0)}
                onUpdate={() => crudProps.onUpdateIvim(item.id)}
                onDelete={() => onDeleteIvim([item.id])}
                onDisable={() => onDisableIvim([item.id])}
                onEnable={() => onEnableIvim([item.id])}
                onClone={() => crudProps.onCloneIvim(item.id)}
                onCheckedChange={handleCheckedChange}
                checked={checked.includes(item.id)}
                showCheckbox={true}
                showIvimPictogram={!!item.metadata.datexii?.description}
                writeAllowed={canWriteIvim}
                blockedActionMode={blockedActionMode}
                blockedMessage={writeBlockedMessage}
              />
            ))}
        {ivims.length === 0 && <>{l10n.getString('forms-ivim-list-empty')}</>}
      </div>
      {ivims.length > 0 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          handleChange={handleChange}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          goToPage={goToPage}
        />
      )}
    </CardContent>
  );
};

export default React.memo(IvimListLayout);
